import { useMemo, useState } from 'react'
import {
  PHONE_COUNTRY_OPTIONS,
  digitsOnly,
  formatE164Phone,
  getPhoneCountryOption,
  splitE164Phone,
} from '../../utils/phoneNumber'
import './phoneNumberInput.css'

type PhoneNumberInputProps = {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  required?: boolean
  label?: string
  selectClassName?: string
  inputClassName?: string
  groupClassName?: string
  localPlaceholder?: string
  testIdPrefix?: string
  formatCountryLabel?: (option: typeof PHONE_COUNTRY_OPTIONS[number]) => string
}

export const PhoneNumberInput = ({
  value,
  onChange,
  disabled = false,
  required = false,
  label,
  selectClassName,
  inputClassName,
  groupClassName,
  localPlaceholder,
  testIdPrefix,
  formatCountryLabel,
}: PhoneNumberInputProps) => {
  const parsed = useMemo(() => splitE164Phone(value), [value])
  const [countryCode, setCountryCode] = useState(parsed.countryCode)
  const [localNumber, setLocalNumber] = useState(parsed.localNumber)
  const [prevValue, setPrevValue] = useState(value)
  if (prevValue !== value) {
    setPrevValue(value)
    setCountryCode(parsed.countryCode)
    setLocalNumber(parsed.localNumber)
  }
  const country = getPhoneCountryOption(countryCode)

  const emit = (nextCountryCode: string, nextLocalNumber: string) => {
    setCountryCode(nextCountryCode)
    setLocalNumber(nextLocalNumber)
    onChange(formatE164Phone(nextCountryCode, nextLocalNumber))
  }

  return (
    <>
      {label ? <span>{label}</span> : null}
      <div className={groupClassName ?? 'phoneNumberInputGroup'}>
        <select
          aria-label="Country code"
          value={countryCode}
          disabled={disabled}
          onChange={(event) => emit(event.target.value, localNumber)}
          className={selectClassName}
          data-testid={testIdPrefix ? `${testIdPrefix}-country` : undefined}
        >
          {PHONE_COUNTRY_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>{formatCountryLabel ? formatCountryLabel(option) : option.label}</option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={localPlaceholder ?? '9876543210'}
          value={localNumber}
          disabled={disabled}
          required={required}
          maxLength={country.digitsMax}
          onChange={(event) => emit(countryCode, digitsOnly(event.target.value).slice(0, country.digitsMax))}
          className={inputClassName}
          data-testid={testIdPrefix ? `${testIdPrefix}-local` : undefined}
        />
      </div>
    </>
  )
}
